import type { ColumnDef } from '@tanstack/react-table'
import { Download } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Label } from 'shared/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/components/ui/select'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'
import { saveBase64File, tashkentMonth } from './per-kg-file'

/**
 * «Килограммы» — the cashier's month of the Per-kg fee (ADR-0034, ADR-0047, tickets
 * 17, 55): TezGo's own revenue, the partner's tariff per kg times the weight of each
 * partner Box the hub took in — its Hub weight, else the partner's — never in any
 * Cashbox. The cashier picks a partner and a calendar month (Tashkent time; last
 * month by default) in one row, reads the month's totals in one line and its lines
 * as a table — the lines without a tariff apart, so none is lost — and downloads the
 * Excel file the server builds, to send the partner outside the system. The running
 * month may be read, marked unfinished. The calls are injected (`listPartners`,
 * `loadSummary`, `loadFile`, `saveFile`, `now`) so the screen is tested without a
 * backend; they default to the real tRPC client and the browser.
 */

export type PerKgPartner = RouterOutputs['partners']['Names'][number]
export type PerKgSummary = RouterOutputs['perKg']['Summary']
export type PerKgExport = RouterOutputs['perKg']['Export']
type PerKgLine = PerKgSummary['lines'][number]

const partnersViaTrpc = () => trpcClient.partners.Names.query()
const summaryViaTrpc = (partnerId: string, month: string) =>
	trpcClient.perKg.Summary.query({ partnerId, month })
const fileViaTrpc = (partnerId: string, month: string) =>
	trpcClient.perKg.Export.query({ partnerId, month })

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

/** How many months back the choice reaches, besides the running one. */
const MONTHS_BACK = 12
const NUMBER = { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' }
/** Lines on one page of the table; the file holds them all. */
const PAGE_SIZE = 50

const kg = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 3 })
const sum = (n: number) => n.toLocaleString('ru-RU')

/** `YYYY-MM` as a person reads it: «сентябрь 2026». */
function readableMonth(month: string): string {
	const [year, m] = month.split('-').map(Number)
	const name = new Date(Date.UTC(year!, m! - 1, 1)).toLocaleDateString('ru-RU', { month: 'long', timeZone: 'UTC' })
	return `${name} ${year}`
}

export interface PerKgProps {
	listPartners?: () => Promise<PerKgPartner[]>
	loadSummary?: (partnerId: string, month: string) => Promise<PerKgSummary>
	loadFile?: (partnerId: string, month: string) => Promise<PerKgExport>
	saveFile?: (file: PerKgExport) => void
	now?: () => Date
}

export function PerKg({
	listPartners = partnersViaTrpc,
	loadSummary = summaryViaTrpc,
	loadFile = fileViaTrpc,
	saveFile = saveBase64File,
	now = () => new Date(),
}: PerKgProps) {
	const { t } = useTranslate()
	const [partners, setPartners] = useState<PerKgPartner[] | null>(null)
	const [partnerId, setPartnerId] = useState('')
	const [month, setMonth] = useState(() => tashkentMonth(now(), 1))
	// The running month and the twelve before it, newest first.
	const [months] = useState(() => Array.from({ length: MONTHS_BACK + 1 }, (_, back) => tashkentMonth(now(), back)))
	const [summary, setSummary] = useState<PerKgSummary | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [downloading, setDownloading] = useState(false)
	const reads = useRef(0)

	useEffect(() => {
		listPartners().then(
			(next) => {
				setPartners(next)
				if (next.length === 1) setPartnerId(next[0]!._id)
			},
			(e) => setError(failure(e)),
		)
	}, [listPartners])

	// The totals are the server's: read afresh whenever the partner or the month changes.
	useEffect(() => {
		if (!partnerId || !month) return
		const read = ++reads.current
		setSummary(null)
		setError(null)
		loadSummary(partnerId, month).then(
			(next) => {
				if (read === reads.current) setSummary(next)
			},
			(e) => {
				if (read === reads.current) setError(failure(e))
			},
		)
	}, [loadSummary, partnerId, month])

	const download = async () => {
		setDownloading(true)
		setError(null)
		try {
			saveFile(await loadFile(partnerId, month))
		} catch (e) {
			setError(failure(e))
		} finally {
			setDownloading(false)
		}
	}

	const billed = summary?.lines.filter((l) => l.amount !== null) ?? []
	const withoutTariff = summary?.lines.filter((l) => l.amount === null) ?? []

	return (
		<div className='flex flex-col gap-6' data-testid='per-kg'>
			<div className='flex flex-wrap items-center gap-x-6 gap-y-3'>
				<div className='flex items-center gap-2'>
					<Label htmlFor='kg-partner'>{t('cashdesk.kgPartner')}</Label>
					<Select value={partnerId} onValueChange={setPartnerId}>
						<SelectTrigger id='kg-partner' className='w-56' data-testid='kg-partner' data-value={partnerId}>
							<SelectValue placeholder={t('cashdesk.kgPartnerPlaceholder')} />
						</SelectTrigger>
						<SelectContent>
							{(partners ?? []).map((p) => (
								<SelectItem key={p._id} value={p._id} data-testid={`kg-partner-${p._id}`}>
									{p.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className='flex items-center gap-2'>
					<Label htmlFor='kg-month'>{t('cashdesk.kgMonth')}</Label>
					<Select value={month} onValueChange={setMonth}>
						<SelectTrigger id='kg-month' className='w-48' data-testid='kg-month' data-value={month}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{months.map((m) => (
								<SelectItem key={m} value={m} data-testid={`kg-month-${m}`}>
									{readableMonth(m)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{error && (
				<p role='alert' className='text-sm text-destructive' data-testid='kg-error'>
					{error}
				</p>
			)}
			{partnerId && !summary && !error && <p className='text-sm text-muted-foreground'>{t('cashdesk.kgLoading')}</p>}

			{summary && (
				<>
					<section className='flex flex-col gap-3' data-testid='kg-summary'>
						<SectionTitle
							title={t('cashdesk.kgTitle')}
							hint={t('cashdesk.kgExplain')}
							aside={
								<>
									{!summary.monthFinished && (
										<Badge
											variant='outline'
											className='border-amber-500/60 text-amber-700 dark:text-amber-400'
											data-testid='kg-unfinished'
										>
											{t('cashdesk.kgUnfinished')}
										</Badge>
									)}
									<Button
										variant='outline'
										size='sm'
										data-testid='kg-download'
										disabled={downloading}
										onClick={download}
									>
										<Download />
										{downloading ? t('cashdesk.kgDownloading') : t('cashdesk.kgDownload')}
									</Button>
								</>
							}
						/>
						<Stats>
							<Stat valueTestId='kg-boxes' label={t('cashdesk.kgBoxes')} value={String(summary.boxes)} />
							{summary.parcels > 0 && (
								<Stat valueTestId='kg-parcels' label={t('cashdesk.kgParcels')} value={String(summary.parcels)} />
							)}
							<Stat valueTestId='kg-total-kg' label={t('cashdesk.kgTotalKg')} value={kg(summary.totalKg)} />
							<Stat
								valueTestId='kg-total-amount'
								label={t('cashdesk.kgTotalAmount')}
								value={sum(summary.totalAmount)}
							/>
						</Stats>
					</section>

					<section className='flex flex-col gap-2'>
						<SectionTitle title={t('cashdesk.kgLinesTitle')} count={billed.length} />
						<KgLines lines={billed} />
					</section>

					<section className='flex flex-col gap-2' data-testid='kg-without-tariff'>
						<SectionTitle
							title={t('cashdesk.kgWithoutTariff')}
							count={withoutTariff.length}
							hint={t('cashdesk.kgWithoutTariffHint')}
						/>
						<WithoutTariff lines={withoutTariff} />
					</section>
				</>
			)}
		</div>
	)
}

/** What a line's notes say, as quiet badges: whose weight it is, why it is unusual. */
function Notes({ line }: { line: PerKgLine }) {
	const { t } = useTranslate()
	const notes = line.notes.filter((n) => n !== 'no_tariff')
	if (notes.length === 0) return null
	return (
		<div className='flex flex-wrap gap-1'>
			{notes.map((n) => (
				<Badge key={n} variant='outline' className='font-normal' data-note={n}>
					{t(`cashdesk.kgNote_${n}`)}
				</Badge>
			))}
		</div>
	)
}

/** The month's billed lines: what the hub took in, its weight, the tariff and the amount. */
function KgLines({ lines }: { lines: PerKgLine[] }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<PerKgLine>[]>(
		() => [
			{
				id: 'at',
				header: t('cashdesk.kgColAt'),
				cell: ({ row }) => <span className='tabular-nums'>{formatDayTime(row.original.at)}</span>,
			},
			{
				id: 'code',
				header: t('cashdesk.kgColCode'),
				cell: ({ row }) => <span className='font-mono'>{row.original.code}</span>,
			},
			{
				id: 'point',
				header: t('cashdesk.kgColPoint'),
				cell: ({ row }) => row.original.pointCode ?? <span className='text-muted-foreground'>—</span>,
			},
			{
				id: 'parcels',
				header: t('cashdesk.kgColParcels'),
				meta: NUMBER,
				cell: ({ row }) => row.original.parcelsInBox ?? <span className='text-muted-foreground'>—</span>,
			},
			{
				id: 'weight',
				header: t('cashdesk.kgColWeight'),
				meta: NUMBER,
				cell: ({ row }) => kg(row.original.weightKg),
			},
			{
				id: 'tariff',
				header: t('cashdesk.kgColTariff'),
				meta: NUMBER,
				cell: ({ row }) => sum(row.original.tariffPerKg ?? 0),
			},
			{
				id: 'amount',
				header: t('cashdesk.kgColAmount'),
				meta: NUMBER,
				cell: ({ row }) => sum(row.original.amount ?? 0),
			},
			{ id: 'notes', header: '', cell: ({ row }) => <Notes line={row.original} /> },
		],
		[t],
	)
	const { table } = useDataTable({
		data: lines,
		columns,
		rowCount: lines.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (l) => `${l.kind}:${l.code}`,
		initialState: { pagination: { pageIndex: 0, pageSize: PAGE_SIZE } },
	})
	return (
		<div data-testid='kg-lines'>
			<DataTable
				table={table}
				withPagination={lines.length > PAGE_SIZE}
				emptyText={t('cashdesk.kgLinesEmpty')}
				row={{ attributes: (l) => ({ 'data-testid': `kg-line-${l.code}` }) }}
			/>
		</div>
	)
}

/** The lines whose pinned policy version sets no tariff: their weight counts, their amount is not in the total. */
function WithoutTariff({ lines }: { lines: PerKgLine[] }) {
	const { t } = useTranslate()
	return (
		<CompactTable testId='kg-without-tariff-lines'>
			<TableHeader>
				<TableRow>
					<TableHead>{t('cashdesk.kgColAt')}</TableHead>
					<TableHead>{t('cashdesk.kgColCode')}</TableHead>
					<TableHead>{t('cashdesk.kgColPoint')}</TableHead>
					<TableHead className='text-right'>{t('cashdesk.kgColWeight')}</TableHead>
					<TableHead />
				</TableRow>
			</TableHeader>
			{lines.length === 0 ? (
				<EmptyTableBody columns={5}>{t('cashdesk.kgWithoutTariffEmpty')}</EmptyTableBody>
			) : (
				<TableBody>
					{lines.map((l) => (
						<TableRow key={`${l.kind}:${l.code}`} data-testid={`kg-no-tariff-${l.code}`}>
							<TableCell className='tabular-nums'>{formatDayTime(l.at)}</TableCell>
							<TableCell className='font-mono'>{l.code}</TableCell>
							<TableCell>{l.pointCode ?? <span className='text-muted-foreground'>—</span>}</TableCell>
							<TableCell className='text-right tabular-nums'>{kg(l.weightKg)}</TableCell>
							<TableCell>
								<Notes line={l} />
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			)}
		</CompactTable>
	)
}
